import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  DollarSign,
  Calendar,
  FileText,
  Plus,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  Clock,
  CheckCircle,
  ArrowRight,
  CreditCard,
  Banknote
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getPayrollRuns, getAllEmployeeSalaries, getActiveLoans } from '../../api/payroll';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import BlurredValue from '../../components/common/BlurredValue';
import PeriodFilter from '../../components/common/PeriodFilter';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';

const PayrollDashboard = () => {
  const navigate = useNavigate();
  const { periodRange, getDateRangeParams } = useFiscalPeriod();
  const [runs, setRuns] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRangeParams();
      const [runsResponse, employeesResponse, loansResponse] = await Promise.all([
        getPayrollRuns({ limit: 100, module: 'retail', startDate, endDate }),
        getAllEmployeeSalaries({ module: 'retail' }),
        getActiveLoans({ module: 'retail', startDate, endDate })
      ]);
      setRuns(
        Array.isArray(runsResponse) ? runsResponse : runsResponse?.data || runsResponse?.runs || []
      );
      setEmployees(
        Array.isArray(employeesResponse)
          ? employeesResponse
          : employeesResponse?.data || employeesResponse?.employees || []
      );
      setLoans(
        Array.isArray(loansResponse)
          ? loansResponse
          : loansResponse?.data || loansResponse?.loans || []
      );
    } catch (error) {
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [getDateRangeParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData, periodRange]);

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700/30 dark:text-gray-300',
      processing: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300',
      pending_approval: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
      approved: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300',
      paid: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300',
      cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={14} />;
      case 'approved':
        return <CheckCircle size={14} />;
      case 'processing':
        return <Clock size={14} />;
      case 'cancelled':
        return <AlertCircle size={14} />;
      default:
        return <Clock size={14} />;
    }
  };

  // Calculate total monthly gross from active paid/approved payroll runs in period
  const paidRuns = runs.filter((r) => r.status === 'paid' || r.status === 'approved');
  const totalMonthlyGross = paidRuns.reduce((sum, r) => sum + parseFloat(r.total_gross || 0), 0);
  const totalLoanBalance = loans.reduce((sum, l) => sum + parseFloat(l.balance || 0), 0);
  const latestRun = runs.length > 0 ? runs[0] : null;

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-[1700px] mx-auto pb-10 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-primary-black">Payroll Dashboard</h1>
            <p className="text-sm text-gray-500">Overview of payroll and loan operations</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PeriodFilter storageKey="payroll_dashboard" />
            <button
              onClick={() => navigate('/payroll/runs', { state: { openCreateModal: true } })}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition"
            >
              <Plus className="w-4 h-4" />
              New Payroll Run
            </button>
            <button
              onClick={fetchData}
              className="p-2 border rounded-lg hover:bg-gray-50 bg-white transition"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            onClick={() => navigate('/payroll/salaries')}
            className="bg-white rounded-lg border border-gray-100 p-6 shadow-sm flex items-center gap-4 cursor-pointer hover:shadow-md transition"
          >
            <div className="p-3 bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 rounded-lg">
              <Users size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Total Employees</p>
              <p className="text-2xl font-bold text-primary-black mt-1">{employees.length}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-100 p-6 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded-lg">
              <TrendingUp size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Monthly Gross</p>
              <p className="text-2xl font-bold text-primary-black mt-1">
                <BlurredValue value={formatCurrency(totalMonthlyGross)} />
              </p>
            </div>
          </div>

          <div
            onClick={() => navigate('/payroll/loans')}
            className="bg-white rounded-lg border border-gray-100 p-6 shadow-sm flex items-center gap-4 cursor-pointer hover:shadow-md transition"
          >
            <div className="p-3 bg-amber-100 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400 rounded-lg">
              <Banknote size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Active Loans</p>
              <p className="text-2xl font-bold text-primary-black mt-1">{loans.length}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Bal: <BlurredValue value={formatCurrency(totalLoanBalance)} />
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-100 p-6 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400 rounded-lg">
              <Calendar size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Latest Run Status</p>
              <p className="text-2xl font-bold text-primary-black mt-1 capitalize">
                {latestRun?.status || 'No runs yet'}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions (Four columns) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/payroll/runs')}
            className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm hover:border-accent-gold/40 hover:shadow-md transition group text-left w-full"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent-gold/10 text-accent-gold rounded-lg">
                  <Plus size={20} />
                </div>
                <div>
                  <p className="font-semibold text-primary-black text-sm">New Payroll Run</p>
                  <p className="text-xs text-gray-400 mt-0.5">Create & process payroll</p>
                </div>
              </div>
              <ArrowRight
                size={16}
                className="text-gray-400 group-hover:text-accent-gold transition"
              />
            </div>
          </button>

          <button
            onClick={() => navigate('/payroll/salaries')}
            className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm hover:border-accent-gold/40 hover:shadow-md transition group text-left w-full"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent-gold/10 text-accent-gold rounded-lg">
                  <Users size={20} />
                </div>
                <div>
                  <p className="font-semibold text-primary-black text-sm">Employee Salaries</p>
                  <p className="text-xs text-gray-400 mt-0.5">Manage structures</p>
                </div>
              </div>
              <ArrowRight
                size={16}
                className="text-gray-400 group-hover:text-accent-gold transition"
              />
            </div>
          </button>

          <button
            onClick={() => navigate('/payroll/loans')}
            className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm hover:border-accent-gold/40 hover:shadow-md transition group text-left w-full"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent-gold/10 text-accent-gold rounded-lg">
                  <CreditCard size={20} />
                </div>
                <div>
                  <p className="font-semibold text-primary-black text-sm">View Loans</p>
                  <p className="text-xs text-gray-400 mt-0.5">Loans & approval flow</p>
                </div>
              </div>
              <ArrowRight
                size={16}
                className="text-gray-400 group-hover:text-accent-gold transition"
              />
            </div>
          </button>

          <button
            onClick={() => navigate('/payroll/payslips')}
            className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm hover:border-accent-gold/40 hover:shadow-md transition group text-left w-full"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent-gold/10 text-accent-gold rounded-lg">
                  <FileText size={20} />
                </div>
                <div>
                  <p className="font-semibold text-primary-black text-sm">Payslips History</p>
                  <p className="text-xs text-gray-400 mt-0.5">View employee payslips</p>
                </div>
              </div>
              <ArrowRight
                size={16}
                className="text-gray-400 group-hover:text-accent-gold transition"
              />
            </div>
          </button>
        </div>

        {/* Recent Payroll Runs */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-primary-black">Recent Payroll Runs</h2>
            <button
              onClick={() => navigate('/payroll/runs')}
              className="text-sm text-accent-gold hover:text-accent-goldDark font-medium transition flex items-center gap-1"
            >
              View All <ArrowRight size={14} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Description
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Period
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Payment Date
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Gross Pay
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Net Pay
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-300">
                          <Calendar size={32} />
                        </div>
                        <div>
                          <p className="font-semibold text-primary-black text-lg">
                            No payroll runs yet
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            Create your first payroll run to get started.
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  runs.map((run) => (
                    <tr
                      key={run.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/payroll/runs/${run.id}`)}
                    >
                      <td className="px-6 py-4">
                        <span className="text-sm font-semibold text-primary-black">
                          {run.description || run.run_number}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar size={14} className="text-gray-400" />
                          {run.pay_period_start && run.pay_period_end
                            ? `${new Date(run.pay_period_start).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} - ${new Date(run.pay_period_end).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`
                            : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {run.payment_date
                          ? new Date(run.payment_date).toLocaleDateString()
                          : 'Not set'}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${getStatusBadge(run.status)}`}
                        >
                          {getStatusIcon(run.status)}
                          {run.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-primary-black">
                        <BlurredValue value={formatCurrency(run.total_gross || 0)} />
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-green-600">
                        <BlurredValue value={formatCurrency(run.total_net || 0)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollDashboard;
